import { Alert, AlertTitle, Dialog } from "@mui/material/";
import React, {  useState } from "react";
import { useParams } from "react-router-dom";

import useRevalidateParent from "../../../../../../hooks/useRevalidateParent";
import useService from "../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../global/ApiRequestErrorHandler";
import ModalButton from "../../../../../global/ModalButton";
import { CommonToastProvider } from "../../../../../modules/Common/ToastProvider/components/CommonToastProvider";
import AlternativeProvidersModal from "../../../../../modules/providers/add-alternative-provider/AlternativeProvidersModal";
import AddCustomProvider from "../../../../../modules/providers/add-alternative-provider/components/AddCustomProvider";
import ProviderDetail from "../../../../../modules/providers/provider-detail";
import ProviderSearch from "../../../../../modules/providers/search-provider/ProviderSearch";
import { useProviderSearchStore } from "../../../../../modules/providers/search-provider/store/providerSearchStore";

import { useProCliStore } from "./ProviderListv2/hooks/useProCliStore";

export default function AddClientProviderModal({
  open,
  setOpen,
  caseInfo,
  isAddProviderModal = true,
}) {
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [assignedProvider, setAssignedProvider] = useState("");
  const [openCustomProv, setOpenCustomProv] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providerDetail, setProviderDetail] = useState(null);
  const [showAlternativesModal, setShowAlternativesModal] = useState(false);
  const [alternativesData, setAlternativesData] = useState(null);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const [providerData, setProviderData] = useState(null);
  const [triggerToast, setTriggerToast] = useState(false);
  const caseRepository = useService("repositories/case-repository");
  const providerRepository = useService("repositories/provider-repository");
  const providerClientStatusRepository = useService(
    "repositories/provider-client-status-repository"
  );
  const { id } = useParams();
  const { setShouldFetchData } = useProCliStore();
  const { isProviderMapPage, reset } = useProviderSearchStore();
  const handleOpenSuccess = () => {
    setOpenSuccess(false);
    setOpen(false);
    setShouldFetchData(true);
    revalidateParent();
    reset();
  };
  const [toastCount, setToastCount] = useState(0);

  // Checks for alternative providers before adding out-of-network provider
  const checkForAlternatives = async (providerId) => {
    if (!providerId) {
      return;
    }

    setLoadingAlternatives(true);
    try {
      const data = await caseRepository.searchForAlternatives(id, {
        id: providerId,
      });

      if (data && data.alternative_providers && data.alternative_providers.length > 0) {
        setAlternativesData(data);
        setProviderData({ providerId });
        setShowAlternativesModal(true);
      }
    } catch (error) {
      if (error.status === 404) {
        handleAddProCliStatus(providerId);
      } else {
        const errArr = ApiRequestErrorHandler(error.data);
        setErrorMessage(errArr);
        setOpenError(true);
      }
    } finally {
      setLoadingAlternatives(false);
    }
  };

  const handleAddNewProvider = async (justification, newProviderData) => {
    try {
      const response = await providerRepository.createRecord({
        ...newProviderData,
      });

      handleAddProCliStatus(response?.id, justification);
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error);
      setErrorMessage(errArr);
      setOpenError(true);
    }
  };

  // Checks for alternatives based on provider types for new providers
  const checkForAlternativesWithTypes = async (providerTypes, newProviderData) => {
    if (!providerTypes) {
      return;
    }

    setLoadingAlternatives(true);
    try {
      const data = await caseRepository.searchForAlternatives(id, {
        provider_types: providerTypes,
      });

      if (data && data.alternative_providers && data.alternative_providers.length > 0) {
        setAlternativesData(data);
        setProviderData(newProviderData);
        setShowAlternativesModal(true);
      }
    } catch (error) {
      if (error.status === 404) {
        handleAddNewProvider(null, newProviderData);
      } else {
        const errArr = ApiRequestErrorHandler(error.data);
        setErrorMessage(errArr);
        setOpenError(true);
      }
    } finally {
      setLoadingAlternatives(false);
    }
  };

  // Closes alternatives modal and clears data
  const handleCloseAlternativesModal = () => {
    setShowAlternativesModal(false);
    setAlternativesData(null);
  };

  // Confirms adding original provider after viewing alternatives
  const handleConfirmOriginalProvider = (justification) => {
    setShowAlternativesModal(false);
    if (providerData?.providerId) {
      handleAddProCliStatus(providerData.providerId, justification);
    } else {
      handleAddNewProvider(justification, providerData);
    }
  };

  // Creates provider-client relationship record
  const handleAddProCliStatus = async (providerId, justification) => {
    setLoading(true);
    const currentCase = await caseRepository.findRecord(id);

    const assignNewProvider = {
      client: caseInfo.client?.pk || currentCase.client?.id,
      provider: providerId || assignedProvider,
      ...(justification ? { out_of_network_justification: justification } : {}),
    };

    try {
      const response = await providerClientStatusRepository.post(assignNewProvider);
      if (response) {
        setOpenSuccess(true);
        setLoading(false);
        setToastCount((prev) => prev = 0);
      }
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error);
      setErrorMessage(errArr);
      setLoading(false);
      setOpenError(true);
      setToastCount((prev) => prev + 1);
    }
  };

  // Selects an alternative provider from the modal
  const handleSelectAlternativeProvider = (providerId) => {
    setShowAlternativesModal(false);
    handleAddProCliStatus(providerId);
  };

  const handleOpenProvModal = () => {
    setOpenCustomProv(!openCustomProv);
  };
  CommonToastProvider(errorMessage[2] ? errorMessage[2].replace("details:", "").trim() : "", true, toastCount);
  return (
    <div
      className={`mt-4 flex ${!isProviderMapPage ? "w-[95%]" : "w-full"} flex-col justify-center`}
    >
      <ProviderSearch
        isModal={isAddProviderModal}
        openModal={open}
        setCloseModal={() => setOpen(false)}
        caseInfo={caseInfo}
        handleOnClickCreateCustomProvider={handleOpenProvModal}
        handleOnClickAddProvider={checkForAlternatives}
      />
      <Dialog open={openSuccess}>
        <Alert variant="outlined" severity="success" role="alert">
          <AlertTitle>Provider Added</AlertTitle>
          <p>
            Successfully Added Provider to Client's Data
            <br />
          </p>
        </Alert>
        <div style={{ padding: "1rem 0" }}>
          <ModalButton action={handleOpenSuccess} baseColor={"#1976d2"} content={"OK"} />
        </div>
      </Dialog>
      <AddCustomProvider
        openCustomProv={openCustomProv}
        setOpenCustomProv={setOpenCustomProv}
        checkForAlternativesWithTypes={checkForAlternativesWithTypes}
      />
      {providerDetail && (
        <ProviderDetail
          open
          providerDetail={providerDetail}
          setOpen={() => setProviderDetail(null)}
        />
      )}
      <AlternativeProvidersModal
        open={showAlternativesModal}
        onClose={handleCloseAlternativesModal}
        onSelectAlternative={handleSelectAlternativeProvider}
        onConfirmOriginalProvider={handleConfirmOriginalProvider}
        alternativesData={alternativesData}
        isLoading={loadingAlternatives}
      />
    </div>
  );
}
